import React from 'react';
import {
	MonitoringCenterCase,
	MonitoringCenterTable,
} from '../Components/MonitoringCenterListTable/MonitoringCenterListTable';
import { showCaseDetail } from '../../Case/Services/case.service';
import { Backlink } from '../../Base/Enums/Backlink.enum';
import LinkButton from '../../../Components/Button/LinkButton';
import { Theme } from '../../../Enums/Theme.enum';
import styles from './MonitoringCenterColumns.module.scss';
import MonitoringCenterPriorityPickerColumn from '../Components/MonitoringCenterItemCols/MonitoringCenterPriorityPickerColumn';
import Text from '../../../Components/Base/Text/Text';
import MonitoringCenterLabelColumn from '../Components/MonitoringCenterItemCols/MonitoringCenterLabelColumn';
import PatientProfileButtonWithVirtualSelection from '../../Patient/Components/PatientProfileButtonWithVirtualSelection/PatientProfileButtonWithVirtualSelection';
import MonitoringCenterAssignedDoctorColumn from '../Components/MonitoringCenterItemCols/MonitoringCenterAssignedDoctorColumn';
import CaseNotePreview from '../../Case/Components/CaseNote/CaseNotePreview';
import CaseNoteButton from '../../Case/Components/CaseNote/CaseNoteButton';
import { FormattedDateTime } from '../../../Components/FormattedDate';
import MonitoringCenterControlsColumn from '../Components/MonitoringCenterItemCols/MonitoringCenterControlsColumn';
import MonitoringCenterAttendingDoctorColumn from '../Components/MonitoringCenterItemCols/MonitoringCenterAttendingDoctorColumn';
import { RequestState } from '../Enums/RequestState.enum';
import { DataTableColumn } from '../../Base/Components/DataTable/DataTable';

export const MonitoringCenterDefaultColumns: DataTableColumn<
	MonitoringCenterCase,
	MonitoringCenterTable
>[] = [
	{
		key: 'number',
		label: 'component.monitoringCenter.tableHead.number',
		renderItem: (item: MonitoringCenterCase) => {
			const handleVisitCase = async (caseId: string) => {
				await showCaseDetail(caseId, Backlink.MONITORING_CENTER);
			};
			return (
				<LinkButton
					title={item.code}
					theme={Theme.BLUE}
					onClick={() => handleVisitCase(item.id)}
					className={styles.numberButton}
				/>
			);
		},
	},
	{
		key: 'priority',
		label: 'component.monitoringCenter.tableHead.priority',
		renderItem: (item: MonitoringCenterCase) => {
			return <MonitoringCenterPriorityPickerColumn item={item} />;
		},
	},
	{
		key: 'state',
		label: 'component.monitoringCenter.tableHead.state',
		renderItem: (item: MonitoringCenterCase) => {
			const CASE_STATE_TRANSLATIONS: Record<string, string> = {
				new: 'component.monitoringCenter.caseStatus.new',
				inProgress: 'component.monitoringCenter.caseStatus.inProgress',
			};
			return (
				<Text
					tx={
						CASE_STATE_TRANSLATIONS[
							item.isNew ? RequestState.NEW : RequestState.IN_PROGRESS
						]
					}
				/>
			);
		},
	},
	{
		key: 'requestType',
		label: 'component.monitoringCenter.tableHead.requestType',
		bold: true,
		renderItem: (item: MonitoringCenterCase) => (
			<MonitoringCenterLabelColumn item={item} />
		),
	},
	{
		key: 'patient',
		label: 'component.monitoringCenter.tableHead.patient',
		renderItem: (item: MonitoringCenterCase) => (
			<PatientProfileButtonWithVirtualSelection
				patientId={item.patient.id}
				isVirtual={!!item.patient.originalId}
				title={item?.patient?.profile?.fullName}
				className={styles.patientButton}
			/>
		),
	},
	{
		key: 'birthNumber',
		label: 'component.monitoringCenter.tableHead.birthNumber',
		renderItem: (item: MonitoringCenterCase) =>
			item?.patient?.profile?.formattedBirthNumber,
	},
	{
		key: 'attendingDoctor',
		label: 'component.monitoringCenter.tableHead.attendingDoctor',
		bold: true,
		renderItem: (item: MonitoringCenterCase) => (
			<MonitoringCenterAttendingDoctorColumn item={item} />
		),
	},
	{
		key: 'assignment',
		label: 'component.monitoringCenter.tableHead.assignment',
		renderItem: (item: MonitoringCenterCase, table: MonitoringCenterTable) => (
			<MonitoringCenterAssignedDoctorColumn
				item={item}
				onRefresh={table.refresh}
			/>
		),
	},
	{
		key: 'workplace',
		label: 'component.monitoringCenter.tableHead.workplace',
		renderItem: (item: MonitoringCenterCase) => item?.workplace?.codeName,
	},
	{
		key: 'diagnosis',
		label: 'component.monitoringCenter.tableHead.diagnosis',
		renderItem: (item: MonitoringCenterCase) => item?.diagnosis,
	},
	{
		key: 'noteForMedics',
		label: 'component.monitoringCenter.tableHead.noteForMedics',
		renderItem: (item: MonitoringCenterCase, table: MonitoringCenterTable) => (
			<>
				<CaseNotePreview text={item?.lastNote?.text} />
				<CaseNoteButton
					note={item?.lastNote}
					className={styles.noteButton}
					caseId={item.id}
					onSuccess={table.refresh}
					firstUpper
				/>
			</>
		),
	},
	{
		key: 'created',
		label: 'component.monitoringCenter.tableHead.created',
		renderItem: (item: MonitoringCenterCase) => (
			<FormattedDateTime datetime={item.createdAt} />
		),
	},
	{
		key: 'lastEdit',
		label: 'component.monitoringCenter.tableHead.lastEdit',
		renderItem: (item: MonitoringCenterCase) => (
			<FormattedDateTime datetime={item.updatedAt} />
		),
	},
	{
		key: 'controls',
		visible: true,
		renderItem: (item: MonitoringCenterCase, table: MonitoringCenterTable) => (
			<MonitoringCenterControlsColumn item={item} onRefresh={table.refresh} />
		),
	},
];
